'use client';

import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface CategoriesProps {
  categories: string[]; // ex: ['Markets', 'Finance', 'Sustainability', ...]
  activeCategory: string; // slug: 'markets', 'finance', ...
  className?: string;
  elementId?: string;
  transparent?: boolean;
}

function slugifyCategory(category: string) {
  return category.toLowerCase().replace(/\s+/g, '-');
}

export default function Categories({
  categories,
  className,
  elementId,
  transparent = false,
}: CategoriesProps) {
  const pathname = usePathname();

  const routeCategory = pathname.split('/')[1];

  const activeSlugCategory = routeCategory || 'markets';

  return (
    <div
      className={cn(
        'relative border-t-[.125rem] border-b-[.125rem] border-white/10 lg:w-full w-full',
        transparent ? 'bg-transparent' : '',
        className
      )}
    >
      <ul className="flex items-center gap-[4.62rem] justify-start lg:overflow-x-auto overflow-x-auto relative container lg:px-0 px-[2rem]">
        {categories?.map((categoryLabel, index) => {
          const slug = slugifyCategory(categoryLabel);
          const isActive = slug === activeSlugCategory;

          return (
            <li
              key={index}
              data-slug={slug}
              className="relative h-[8.125rem] flex flex-col items-center justify-center group"
            >
              <CategoryItem elementId={elementId} slug={slug}>
                <span
                  className={cn(
                    'font-inter text-[1.75rem] transition-opacity duration-300 font-medium tracking-[-0.06rem] uppercase group-hover:opacity-80',
                    isActive ? 'text-white' : 'text-white/40'
                  )}
                >
                  {categoryLabel}
                </span>

                {isActive && (
                  <div className="absolute bottom-0 w-full h-[0.5rem] bg-secondary" />
                )}
              </CategoryItem>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CategoryItem({
  slug,
  children,
  elementId,
}: {
  slug: string;
  children: React.ReactNode;
  elementId?: string;
}) {
  const href = elementId ? `/${slug}#${elementId}` : `/${slug}`;

  return (
    <Link
      href={href}
      prefetch
      className="w-full h-full flex items-center justify-center cursor-pointer relative"
    >
      {children}
    </Link>
  );
}
